import { FC, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Tag,
  InlineLoading,
  InlineNotification,
  Pagination,
} from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { ColumnDef } from '@tanstack/react-table';
import { useAtom } from 'jotai';
import { useHistory } from 'react-router-dom';
import { PageHeader } from 'components/page-header';
import { DataTable } from 'components/data-table';
import { FormattedCount } from 'components/formatted-count';
import { RunBatchModal } from './run-batch-modal';
import { ProvisioningSummaryCards } from './summary-cards';
import {
  provisioningBatchesAtom,
  provisioningStatsAtom,
  batchFilterAtom,
} from './atoms';
import {
  statusTagColors,
  statusLabels,
  batchModeLabels,
  batchModeTagColors,
  type ProvisioningBatch,
  type BatchStatus,
  type BatchMode,
  type CountyOption,
} from './types';
import { STATUS_FILTER_OPTIONS, LOOKBACK_OPTIONS } from './utils';
import styles from './page.module.scss';

type FilterOption = { id: string; label: string };

const batchColumns: ColumnDef<ProvisioningBatch>[] = [
  {
    id: 'id',
    accessorKey: 'id',
    header: 'ID',
    size: 60,
    cell: (info) => (
      <span className={styles.batchId}>{String(info.getValue())}</span>
    ),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cell: (info) => {
      const status = info.getValue() as BatchStatus;
      return (
        <Tag type={statusTagColors[status]} size="sm">
          {statusLabels[status]}
        </Tag>
      );
    },
  },
  {
    id: 'mode',
    accessorKey: 'mode',
    header: 'Mode',
    size: 130,
    cell: (info) => {
      const mode = (info.getValue() as BatchMode) || 'provision';
      return (
        <Tag type={batchModeTagColors[mode]} size="sm">
          {batchModeLabels[mode]}
        </Tag>
      );
    },
  },
  {
    id: 'county',
    accessorKey: 'countyName',
    header: 'County',
    size: 180,
    cell: (info) => info.getValue() || '--',
  },
  {
    id: 'state',
    accessorKey: 'stateName',
    header: 'State',
    size: 180,
    cell: (info) => info.getValue() || '--',
  },
  {
    id: 'progress',
    header: 'Progress',
    size: 120,
    enableSorting: false,
    cell: (info) => {
      const batch = info.row.original;
      const done =
        batch.completedCount + batch.failedCount + batch.skippedCount;
      return (
        <span className={styles.progressText}>
          {done} / {batch.totalCount} ({batch.progressPercentage}%)
        </span>
      );
    },
  },
  {
    id: 'completed',
    accessorKey: 'completedCount',
    header: 'Completed',
    size: 115,
    cell: (info) => <FormattedCount value={info.getValue() as number} />,
  },
  {
    id: 'failed',
    accessorKey: 'failedCount',
    header: 'Failed',
    size: 85,
    cell: (info) => {
      const count = info.getValue() as number;
      return count > 0 ? (
        <span className={styles.failedCount}>
          <FormattedCount value={count} />
        </span>
      ) : (
        <span className={styles.zeroCount}>0</span>
      );
    },
  },
  {
    id: 'skipped',
    accessorKey: 'skippedCount',
    header: 'Skipped',
    size: 95,
    cell: (info) => {
      const count = info.getValue() as number;
      return count > 0 ? (
        <FormattedCount value={count} />
      ) : (
        <span className={styles.zeroCount}>0</span>
      );
    },
  },
  {
    id: 'entities',
    accessorKey: 'entitiesCreatedCount',
    header: 'Entities',
    size: 95,
    cell: (info) => <FormattedCount value={info.getValue() as number} />,
  },
  {
    id: 'locations',
    accessorKey: 'locationsCreatedCount',
    header: 'Locations',
    size: 105,
    cell: (info) => <FormattedCount value={info.getValue() as number} />,
  },
  {
    id: 'duration',
    header: 'Duration',
    size: 110,
    enableSorting: false,
    cell: (info) => {
      const { startedAt, completedAt } = info.row.original;
      if (!startedAt) return <span className={styles.muted}>--</span>;
      if (!completedAt) return <span className={styles.muted}>Running...</span>;
      const mins = Math.round(
        (new Date(completedAt).getTime() - new Date(startedAt).getTime()) /
          1000 /
          60
      );
      return `${mins} min`;
    },
  },
  {
    id: 'runBy',
    accessorKey: 'runByName',
    header: 'Created By',
    size: 130,
    cell: (info) =>
      (info.getValue() as string) || (
        <span className={styles.muted}>System</span>
      ),
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Created At',
    size: 170,
    meta: { type: 'datetime' },
  },
];

export const ProvisioningPage: FC = () => {
  const history = useHistory();
  const [batchesLoadable, refreshBatches] = useAtom(provisioningBatchesAtom);
  const [statsLoadable, refreshStats] = useAtom(provisioningStatsAtom);
  const [filters, setFilters] = useAtom(batchFilterAtom);
  const [runModalOpen, setRunModalOpen] = useState(false);

  const [statusFilter, setStatusFilter] = useState<string>(
    filters.status || 'all'
  );
  const [lookbackFilter, setLookbackFilter] = useState<string>(
    filters.lookbackHours ? String(filters.lookbackHours) : 'all'
  );
  const [countyFilter, setCountyFilter] = useState<string>(
    filters.countyId ? String(filters.countyId) : 'all'
  );

  useEffect(() => {
    setFilters((prev) => ({
      status: statusFilter === 'all' ? null : statusFilter,
      countyId: countyFilter === 'all' ? null : parseInt(countyFilter, 10),
      lookbackHours:
        lookbackFilter === 'all' ? null : parseInt(lookbackFilter, 10),
      page: 1,
      perPage: prev.perPage,
    }));
  }, [statusFilter, lookbackFilter, countyFilter, setFilters]);

  const handleRefresh = () => {
    refreshBatches();
    refreshStats();
  };

  const handleBatchCreated = () => {
    setRunModalOpen(false);
    handleRefresh();
  };

  const countyFilterOptions = useMemo(() => {
    const base = [{ id: 'all', label: 'All Counties' }];
    if (statsLoadable.state !== 'hasData' || !statsLoadable.data) return base;
    const counties = statsLoadable.data.counties || [];
    return [
      ...base,
      ...counties.map((c: CountyOption) => ({
        id: String(c.id),
        label: `${c.name}${c.stateName ? `, ${c.stateName}` : ''}`,
      })),
    ];
  }, [statsLoadable]);

  const batches =
    batchesLoadable.state === 'hasData'
      ? batchesLoadable.data?.batches || []
      : [];
  const isLoading = batchesLoadable.state === 'loading';
  const hasError = batchesLoadable.state === 'hasError';

  const stats =
    statsLoadable.state === 'hasData' && statsLoadable.data
      ? statsLoadable.data
      : null;

  const itemToString = (item: FilterOption | null) => item?.label || '';
  const onDropdownChange =
    (setter: (id: string) => void) =>
    ({ selectedItem }: { selectedItem: FilterOption }) =>
      setter(selectedItem.id);

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Bulk Provision"
        actions={
          <div className={styles.headerActions}>
            <Dropdown
              id="county-filter-dropdown"
              titleText="County"
              label="Filter by county"
              items={countyFilterOptions}
              itemToString={itemToString}
              selectedItem={
                countyFilterOptions.find((o) => o.id === countyFilter) ||
                countyFilterOptions[0]
              }
              onChange={onDropdownChange(setCountyFilter)}
              size="sm"
              className={styles.filterDropdown}
            />
            <Dropdown
              id="status-filter-dropdown"
              titleText="Status"
              label="Filter by status"
              items={STATUS_FILTER_OPTIONS}
              itemToString={itemToString}
              selectedItem={
                STATUS_FILTER_OPTIONS.find((o) => o.id === statusFilter) ||
                STATUS_FILTER_OPTIONS[0]
              }
              onChange={onDropdownChange(setStatusFilter)}
              size="sm"
              className={styles.filterDropdown}
            />
            <Dropdown
              id="lookback-dropdown"
              titleText="Lookback Period"
              label="Select time range"
              items={LOOKBACK_OPTIONS}
              itemToString={itemToString}
              selectedItem={
                LOOKBACK_OPTIONS.find((o) => o.id === lookbackFilter) ||
                LOOKBACK_OPTIONS[0]
              }
              onChange={onDropdownChange(setLookbackFilter)}
              size="sm"
              className={styles.filterDropdown}
            />
            <Button
              kind="primary"
              size="sm"
              renderIcon={Add}
              onClick={() => setRunModalOpen(true)}
            >
              Run Batch
            </Button>
          </div>
        }
      />

      <div className={styles.summaryCardsWrapper}>
        <ProvisioningSummaryCards stats={stats ?? undefined} />
      </div>

      {hasError && (
        <InlineNotification
          kind="error"
          title="Failed to load batches"
          subtitle="Check your connection and try refreshing."
          lowContrast
          hideCloseButton={false}
        />
      )}
      {isLoading ? (
        <InlineLoading description="Loading batches..." />
      ) : (
        <>
          <DataTable
            data={batches}
            columns={batchColumns}
            getRowId={(row) => String(row.id)}
            persistenceKey="provisioning-batches-table"
            initialTableState={{
              sortColumn: 'createdAt',
              sortDirection: 'desc',
            }}
            config={{
              columnResizing: true,
              columnReordering: true,
              columnPinning: true,
              sorting: true,
              columnVisibility: true,
              globalSearch: true,
              columnFilters: true,
            }}
            onSelectionChange={(s) => {
              const id = s.selection.toggledNodes[0];
              if (id) history.push(`/admin/provisioning_batches/${id}`);
            }}
          />

          <Pagination
            page={filters.page}
            pageSize={filters.perPage}
            pageSizes={[25, 50, 100]}
            totalItems={
              batchesLoadable.state === 'hasData'
                ? (batchesLoadable.data?.pagination.totalCount ?? 0)
                : 0
            }
            onChange={({
              page,
              pageSize,
            }: {
              page: number;
              pageSize: number;
            }) => {
              setFilters((prev) => ({
                ...prev,
                page,
                perPage: pageSize,
              }));
            }}
          />
        </>
      )}

      <RunBatchModal
        open={runModalOpen}
        onClose={() => setRunModalOpen(false)}
        onCreated={handleBatchCreated}
      />
    </>
  );
};
